import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartDiscountUpdateEvent } from '@shopify/events';

/**
 * Grauv "New Launch Offer" cart banner.
 *
 * - Draws the live progress bar towards the free gift (the threshold is set in the
 *   shop currency and converted to the shopper's market currency).
 * - Auto-applies the offer code once per session (never over another code) and offers a
 *   one-tap "Apply" button as a fallback. Both only work from the threshold up.
 * - Keeps in step with the cart drawer / cart page whenever they re-render.
 */

const AUTO_KEY = 'grauv-offer-auto:';
const BAD_KEY = 'grauv-offer-ineligible:';
const GIFT_KEY = 'grauv-offer-gift:';

/** @param {{ type: string; before?: string }} detail */
function celebrate(detail) {
  // @ts-ignore - exposed by grauv-cart-celebrate.js
  if (typeof window.grauvCelebrate === 'function') window.grauvCelebrate(detail);
  else {
    // @ts-ignore
    (window.__grauvCelebrateQueue = window.__grauvCelebrateQueue || []).push(detail);
  }
}

const store = {
  /** @param {string} key */
  get(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  /**
   * @param {string} key
   * @param {string} value
   */
  set(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable: ignore */
    }
  },
};

const GIFT_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v9H5v-9M7.5 8a2.5 2.5 0 1 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 1 1 0 5"/></svg>';
const CHECK_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5"/><path d="M7.8 12.4l2.9 2.9 5.5-5.8"/></svg>';

let applying = false;

class GrauvCartOffer extends HTMLElement {
  static get observedAttributes() {
    return ['data-sig'];
  }

  connectedCallback() {
    this.refresh();
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== newValue) this.refresh();
  }

  get code() {
    return (this.dataset.code || '').toUpperCase();
  }

  /** Re-draws the dynamic parts and runs the auto-apply check. */
  refresh() {
    const metrics = this.#metrics();
    this.#renderThreshold(metrics);
    this.#renderGift(metrics);
    this.#renderMessage();
    this.#renderButtons(metrics);
    this.#maybeAutoApply();
  }

  #metrics() {
    const shopCurrency = this.dataset.shopCurrency || '';
    const cartCurrency = this.dataset.currency || shopCurrency;
    const shopifyRate = parseFloat(window.Shopify?.currency?.rate || '1');
    const rate = cartCurrency !== shopCurrency && shopifyRate > 0 ? shopifyRate : 1;
    const threshold = parseFloat(this.dataset.threshold || '0') || 0;
    const target = Math.round(threshold * rate);
    const eligible = (parseFloat(this.dataset.eligible || '0') || 0) / 100;
    return { currency: cartCurrency, target, eligible };
  }

  /**
   * @param {number} value
   * @param {string} currency
   */
  #money(value, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value);
    } catch (e) {
      return `${currency} ${Math.round(value).toLocaleString()}`;
    }
  }

  /** @param {{ currency: string; target: number }} metrics */
  #renderThreshold({ currency, target }) {
    this.querySelectorAll('[data-grauv-threshold]').forEach((el) => {
      el.textContent = this.#money(target, currency);
    });
  }

  /** @param {{ currency: string; target: number; eligible: number }} metrics */
  #renderGift({ currency, target, eligible }) {
    const box = this.querySelector('[data-grauv-offer-gift]');
    if (!(box instanceof HTMLElement)) return;

    const unlocked = this.dataset.giftActive === 'true' || (target > 0 && eligible >= target - 0.5);
    const remaining = Math.max(0, Math.ceil(target - eligible));
    const pct = unlocked ? 100 : target > 0 ? Math.min(100, Math.max(0, (eligible / target) * 100)) : 0;
    const mode = unlocked ? 'unlocked' : 'locked';
    const rebuild = box.dataset.mode !== mode;

    box.classList.toggle('is-unlocked', unlocked);

    if (rebuild) {
      box.dataset.mode = mode;
      const tag =
        document.querySelector('[data-grauv-celebrate-config]')?.getAttribute('data-gift-tag') || 'FREE GIFT';
      box.innerHTML = unlocked
        ? `<div class="grauv-offer__ticket">
            <span class="grauv-offer__ticket-icon">${GIFT_ICON}</span>
            <div class="grauv-offer__ticket-body">
              <strong data-tag></strong>
              <span><span data-text></span> <span class="grauv-offer__gift-sub" data-sub></span></span>
            </div>
          </div>
          <div class="grauv-offer__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100">
            <span class="grauv-offer__bar-fill"></span><span class="grauv-offer__bar-gift">${GIFT_ICON}</span>
          </div>`
        : `<div class="grauv-offer__gift-text">
            <span data-text></span>
          </div>
          <div class="grauv-offer__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}">
            <span class="grauv-offer__bar-fill"></span><span class="grauv-offer__bar-gift">${GIFT_ICON}</span>
          </div>`;
      const tagEl = box.querySelector('[data-tag]');
      if (tagEl) tagEl.textContent = tag;
    }

    const text = box.querySelector('[data-text]');
    if (text) {
      text.textContent = '';
      const template = unlocked ? this.dataset.textUnlocked || '' : this.dataset.textLocked || '';
      const parts = template.split('{amount}');
      text.append(parts[0] || '');
      if (parts.length > 1) {
        const strong = document.createElement('strong');
        strong.textContent = this.#money(remaining, currency);
        text.append(strong, parts.slice(1).join('{amount}'));
      }
    }

    const sub = box.querySelector('[data-sub]');
    if (sub) sub.textContent = unlocked ? this.dataset.textUnlockedSub || '' : '';

    const bar = box.querySelector('.grauv-offer__bar');
    if (bar) bar.setAttribute('aria-valuenow', String(Math.round(pct)));
    const fill = box.querySelector('.grauv-offer__bar-fill');
    if (fill instanceof HTMLElement) {
      if (rebuild) fill.style.width = '0%';
      requestAnimationFrame(() => requestAnimationFrame(() => (fill.style.width = `${pct}%`)));
    }

    // Celebrate the moment the free gift unlocks (once per unlock).
    if (unlocked) {
      if (store.get(GIFT_KEY) !== '1') {
        store.set(GIFT_KEY, '1');
        celebrate({ type: 'gift' });
      }
    } else if (store.get(GIFT_KEY) === '1') {
      store.set(GIFT_KEY, '0');
    }
  }

  /** The code only works from the threshold up, so its apply buttons stay hidden until then. */
  #isEligible() {
    const { target, eligible } = this.#metrics();
    return target <= 0 || eligible >= target - 0.5;
  }

  /** @param {{ target: number; eligible: number }} metrics */
  #renderButtons({ target, eligible }) {
    const open = target <= 0 || eligible >= target - 0.5;
    this.querySelectorAll('[data-grauv-offer-apply]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = !open;
    });
  }

  #renderMessage() {
    const msg = this.querySelector('[data-grauv-offer-msg]');
    if (!(msg instanceof HTMLElement)) return;
    const show = this.dataset.state !== 'applied' && store.get(BAD_KEY + this.code) === '1';
    msg.hidden = !show;
    msg.textContent = show ? this.dataset.textIneligible || '' : '';
  }

  #maybeAutoApply() {
    if (this.dataset.autoApply !== 'true' || this.dataset.state !== 'idle' || !this.#isEligible()) return;
    if ((parseInt(this.dataset.count || '0', 10) || 0) < 1 || applying) return;
    if (store.get(AUTO_KEY + this.code)) return;

    store.set(AUTO_KEY + this.code, '1');
    setTimeout(() => this.apply(), 300);
  }

  /** Applies the offer code (replacing any other code, as only one code can be used at a time). */
  async apply() {
    const code = this.code;
    if (!code || applying || !this.#isEligible()) return;
    applying = true;
    this.classList.add('is-loading');

    const sectionId = this.closest('cart-items-component')?.dataset.sectionId;
    const mode = this.closest('theme-drawer') ? 'hydration' : 'full';
    const before = document.querySelector('.cart-totals__total-value')?.textContent?.trim() || '';
    let success = false;
    const previous = (this.dataset.appliedCodes || '').split('|').filter(Boolean);
    const deferred = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: [{ code }], promise: deferred.promise }));

    /** @param {string} discount */
    const post = async (discount) => {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount, sections: sectionId ? [sectionId] : undefined }),
      });
      const response = await fetch(Theme.routes.cart_update_url, config);
      return response.json();
    };

    try {
      let data = await post(code);
      const entry = (data.discount_codes || []).find(
        (/** @type {{ code: string; applicable: boolean }} */ d) => String(d.code).toUpperCase() === code
      );

      if (!entry || entry.applicable === false) {
        // Not valid for this shopper (e.g. not a first-time customer): put the cart back as it was.
        store.set(BAD_KEY + code, '1');
        data = await post(previous.join(','));
      } else {
        store.set(BAD_KEY + code, '0');
        success = true;
      }

      deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
      const html = sectionId ? data.sections?.[sectionId] : null;
      if (sectionId && html) {
        try {
          await morphSection(sectionId, html, { mode });
        } catch (e) {
          /* the cart still updates on next render */
        }
      }
      if (success) celebrate({ type: 'discount', before });
    } catch (error) {
      deferred.reject(error);
    } finally {
      applying = false;
      this.classList.remove('is-loading');
      // The banner may have been re-rendered; refresh every banner on the page.
      requestAnimationFrame(() => {
        document.querySelectorAll('grauv-cart-offer').forEach((el) => {
          if (el instanceof GrauvCartOffer) el.refresh();
        });
      });
    }
  }
}

if (!customElements.get('grauv-cart-offer')) customElements.define('grauv-cart-offer', GrauvCartOffer);

if (!window.__grauvOfferBound) {
  window.__grauvOfferBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-grauv-offer-apply]');
    if (!button) return;
    const offer = button.closest('grauv-cart-offer');
    if (offer instanceof GrauvCartOffer) {
      event.preventDefault();
      // A manual tap always works, even if auto-apply was tried (or failed) before.
      offer.apply();
    }
  });
}
